import type { Book, BookStatus, Stage } from "../api/types";
import { formatReadingTime } from "./format";

export const STAGE_LABELS: Record<Stage, string> = {
  VALIDATE: "Validating link",
  FETCH_METADATA: "Fetching video info",
  FETCH_TRANSCRIPT: "Fetching transcript",
  PREPROCESS: "Preparing transcript",
  GUARDRAILS: "Screening for prompt injection",
  OUTLINE: "Planning chapters",
  WRITE: "Writing",
  VERIFY: "Checking fidelity",
  ASSEMBLE: "Assembling book",
  SUMMARIZE: "Writing the summary",
  RENDER: "Rendering files",
  COMPLETED: "Completed",
};

export interface StepperStep {
  key: string;
  label: string;
  stages: Stage[];
}

/** The twelve pipeline stages (§3) grouped into the eight friendly steps the stepper shows (§7.2). */
export const STEPPER_STEPS: StepperStep[] = [
  { key: "fetch", label: "Fetching transcript", stages: ["VALIDATE", "FETCH_METADATA", "FETCH_TRANSCRIPT", "PREPROCESS"] },
  { key: "guardrails", label: "Screening for prompt injection", stages: ["GUARDRAILS"] },
  { key: "outline", label: "Planning chapters", stages: ["OUTLINE"] },
  { key: "write", label: "Writing", stages: ["WRITE"] },
  { key: "verify", label: "Checking fidelity", stages: ["VERIFY"] },
  { key: "assemble", label: "Assembling book", stages: ["ASSEMBLE"] },
  { key: "summarize", label: "Writing the summary", stages: ["SUMMARIZE"] },
  { key: "render", label: "Rendering files", stages: ["RENDER"] },
];

/** Index into STEPPER_STEPS of the currently active step; -1 if not started, STEPPER_STEPS.length if past the last one. */
export function stepperIndexForStage(stage: Stage | null): number {
  if (!stage) return -1;
  const idx = STEPPER_STEPS.findIndex((step) => step.stages.includes(stage));
  return idx === -1 ? STEPPER_STEPS.length : idx;
}

export function currentChapterOf(chaptersDone: number, chaptersTotal: number | null): number {
  const next = chaptersDone + 1;
  return chaptersTotal != null ? Math.min(next, chaptersTotal) : next;
}

/** "Chapter 3 of 7" — used under the active step in the stage stepper. */
export function chapterCounterLabel(chaptersDone: number, chaptersTotal: number | null): string {
  const current = currentChapterOf(chaptersDone, chaptersTotal);
  return chaptersTotal != null ? `Chapter ${current} of ${chaptersTotal}` : `Chapter ${current}`;
}

/** "Writing chapter 3/7" / "Checking chapter 3/7" style label for WRITE/VERIFY stages. */
export function chapterProgressLabel(stage: Stage, chaptersDone: number, chaptersTotal: number | null): string {
  const current = currentChapterOf(chaptersDone, chaptersTotal);
  const total = chaptersTotal != null ? String(chaptersTotal) : "?";
  const verb = stage === "WRITE" ? "Writing" : "Checking";
  return `${verb} chapter ${current}/${total}`;
}

type ProgressBook = Pick<
  Book,
  "status" | "stage" | "chapters_done" | "chapters_total" | "reading_minutes" | "error_message"
>;

/** Short status-chip text, e.g. "Writing chapter 3/7", "Completed · 42 min read", "Queued". */
export function progressText(book: ProgressBook): string {
  if (book.status === "queued") return "Queued";
  if (book.status === "processing") {
    if (!book.stage) return "Starting…";
    if (book.stage === "WRITE" || book.stage === "VERIFY") {
      return chapterProgressLabel(book.stage, book.chapters_done, book.chapters_total);
    }
    return STAGE_LABELS[book.stage];
  }
  if (book.status === "completed") {
    const reading = formatReadingTime(book.reading_minutes);
    return reading ? `Completed · ${reading}` : "Completed";
  }
  if (book.status === "canceled") return "Canceled";
  return "Failed";
}

export function isTerminalStatus(status: BookStatus): boolean {
  return status === "completed" || status === "failed" || status === "canceled";
}
